import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { Idea } from '@models/Sessions/Idea/Idea';
import _ from 'lodash';
import * as io from 'socket.io-client';

import { Session } from '../../models/Sessions/Session';

@Injectable()
export class IdeaSocketService {
  private socket: io.Socket;
  private listeners: any[];

  connect(token: string) {
    if (token && !this.socket) {
      this.socket = io.connect(environment.backend + '/session/idea', {
        path: '/socket.io-client',
        query: { token },
      });

      // this.sessionSocket.on('error', err => Raven.captureMessage(err));
      this.socket.on('error', err => console.error(err));
      this.listeners = [];
    }
  }

  disconnect() {
    if (this.socket && this.socket.connected) {
      this.socket.disconnect();
      this.socket = undefined;
    }
  }

  /**
   * Register listeners to sync an ideation session with updates on the model
   * Takes the ideation session we want to sync.
   * Please note that this uses a session as reference.
   * @param session
   */
  syncUpdates(session: Session, existingIdea?: Idea) {
    const getIdea = id => {
      if (existingIdea) {
        return existingIdea;
      }
      return _.find(session.data.ideas, idea => idea._id === id);
    };
    const addListener = (event, callback) => {
      this.socket.on(event, callback);
      this.listeners.push(event);
    };

    // Listen on all events
    // Ideas
    addListener('idea:position', update => {
      const idea = getIdea(update._id);
      idea.position = update.position;
    });

    addListener('idea:update', update => {
      const idea = getIdea(update._id);
      idea.title = update.idea.title;
      idea.one_liner = update.idea.one_liner;
      idea.description = update.idea.description;
      idea.thumbnail_id = update.idea.thumbnail_id;
      idea.benefits = update.idea.benefits;
      idea.challenges = update.idea.challenges;
      idea.last_changed = update.idea.last_changed;
      idea.textScript = update.idea.textScript;
      idea.phoneticScript = update.idea.phoneticScript;
    });

    // Maturity
    addListener('idea:mature', update => {
      const idea = getIdea(update._id);
      idea.mature = update.mature;
    });

    // Benefits
    addListener('idea-benefit:create', update => {
      const idea = getIdea(update._id);
      idea.benefits.push(update.benefit);
    });

    addListener('idea-benefit:delete', update => {
      const idea = getIdea(update._id);
      _.remove(idea.benefits, { _id: update.benefit_id });
    });

    // Challenges
    addListener('idea-challenge:create', update => {
      const idea = getIdea(update._id);
      idea.challenges.push(update.challenge);
    });

    addListener('idea-challenge:delete', update => {
      const idea = getIdea(update._id);
      _.remove(idea.challenges, { _id: update.challenge_id });
    });

    // Solution
    addListener('idea-challenge-solution:create', update => {
      const idea = getIdea(update._id);
      const challenge = _.find(idea.challenges, { _id: update.challenge_id });
      challenge.solutions.push(update.solution);
    });

    addListener('idea-challenge-solution:delete', update => {
      const idea = getIdea(update._id);
      const challenge = _.find(idea.challenges, { _id: update.challenge_id });
      _.remove(challenge.solutions, { _id: update.solution_id });
    });

    addListener('idea-challenge-solution:accept', update => {
      const idea = getIdea(update._id);
      const challenge = _.find(idea.challenges, { _id: update.challenge_id });
      const solution: any = _.find(challenge.solutions, {
        _id: update.solution_id,
      });
      solution.accepted = true;
    });

    addListener('idea-challenge-solution:unaccept', update => {
      const idea = getIdea(update._id);
      const challenge = _.find(idea.challenges, { _id: update.challenge_id });
      const solution: any = _.find(challenge.solutions, {
        _id: update.solution_id,
      });
      solution.accepted = false;
    });

    // Votes
    addListener('idea-voting:update', update => {
      const idea = getIdea(update._id);
      idea.voting.x = update.voting.x;
      idea.voting.y = update.voting.y;
      // $rootScope.$emit('redraw-matrix');
    });

    // Idea-Comments
    addListener('idea-comment:create', update => {
      const idea = getIdea(update._id);
      idea.comments.push(update.comment);
    });

    addListener('idea-comment:delete', update => {
      const idea = getIdea(update._id);
      _.remove(idea.comments, { _id: update.comment_id });
    });

    // Collaborators
    addListener('idea-collaborator:create', update => {
      const idea = getIdea(update._id);
      idea.collaborators.push(update.collaborator);
      idea.collaboratorRequests = idea.collaboratorRequests.filter(
        r =>
          r.participant_id.toString() !==
          update.collaborator.participant_id.toString(),
      );
      idea.collaboratorInvites = idea.collaboratorInvites.filter(
        r =>
          r.participant_id.toString() !==
          update.collaborator.participant_id.toString(),
      );
    });

    addListener('idea-collaborator:delete', update => {
      const idea = getIdea(update._id);
      _.remove(idea.collaborators, { _id: update.collaborator_id });
    });

    addListener('idea-collaborationRequest:create', update => {
      const idea = getIdea(update._id);
      if (!idea.collaboratorRequests) {
        idea.collaboratorRequests = [];
      }
      idea.collaboratorRequests.push(update.newRequest);
    });

    addListener('idea-collaborationRequest:delete', update => {
      const idea = getIdea(update._id);
      _.remove(idea.collaboratorRequests, {
        participant_id: update.participant_id,
      });
    });

    addListener('idea-collaboration-invite:create', update => {
      const idea = getIdea(update._id);
      idea.collaboratorInvites = idea.collaboratorInvites || [];
      idea.collaboratorInvites.push({
        participant_id: update.participant_id,
        user: session.config.participants.find(
          p => p._id.toString() === update.participant_id.toString(),
        ).user,
      });
    });

    addListener('idea-collaboration-invite:delete', update => {
      const idea = getIdea(update._id);
      idea.collaboratorInvites = idea.collaboratorInvites.filter(
        i => i.participant_id.toString() !== update.participant_id.toString(),
      );
    });

    // Follow
    addListener('idea-follow:create', update => {
      const idea = getIdea(update._id);
      idea.followers.push(update);
      if (update.participant_id === session.me._id) {
        idea.i_follow = true;
      }
    });

    addListener('idea-follow:delete', update => {
      const idea = getIdea(update._id);
      _.remove(idea.followers, { participant_id: update.participant_id });
      if (update.participant_id === session.me._id) {
        idea.i_follow = false;
      }
    });

    addListener('idea-audio-project:create', update => {
      const idea = getIdea(update.idea_id);
      idea.audio_project_id = update.audio_project_id;
    });
  }

  /**
   * Removes listeners for updates on the socket
   */
  unsyncUpdates() {
    if (this.socket) {
      _.forEach(this.listeners, event => this.socket.removeListener(event));
      this.listeners = [];
    }
  }
}
